import React, { useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useDispatch, useSelector } from 'react-redux';
import { getBidList } from '../../../apis';
import { showSnackbar } from '../../../store/slices/common.slice';
import { APIResponse, ListingResponse, TypeSnackbarPayload } from '../../../utilities/types';
import DatagridLoader from '../../elements/datagrid-loader.element';
import { getFormattedDate, getFormattedPrice } from '../../../utilities/helper';
import useAuctionBroadcastWsHook from '../../../hooks/auction-broadcast-ws.hook';
import { RootState } from '../../../store';

interface AuctionBidGridWidgetProps {
  auction_id: number;
  viewType?: 'seller' | 'admin';
}

const AuctionBidGridWidget: React.FC<AuctionBidGridWidgetProps> = ({ auction_id, viewType = 'admin' }) => {
  const dispatch = useDispatch();

  const { token } = useSelector((state: RootState) => state.auth);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowCount, setRowCount] = useState(0);

  const pageSize = 20;

  // Simulate API call (replace with your real API)
  const fetchData = useCallback(
    async (pageNumber: number) => {
      setLoading(true);
      try {
        const response = (await getBidList({
          page: pageNumber + 1,
          limit: pageSize,
          filter: { auction_id },
        })) as APIResponse<ListingResponse>;
        if (response.status) {
          const items: any = (Array.isArray(response.data?.items) ? response.data.items : []).map(
            (item: any) => {
              return {
                ...item,
                name: item?.bidder.name,
                company_name: item?.bidder.company_name,
                mobile_number: item?.bidder.mobile_number,
              };
            }
          );
          setRows(items);
          setRowCount(response.data?.pagination?.total);
        }
      } catch (err: any) {
        const snakbarpayload: TypeSnackbarPayload = { severity: 'error', message: err.message };
        if (err?.data?.message) {
          snakbarpayload.message = err?.data?.message;
        } else if (err?.statusText) {
          snakbarpayload.message = err?.statusText;
        }
        dispatch(showSnackbar(snakbarpayload));
      }
      setLoading(false);
    },
    [dispatch, pageSize, auction_id]
  );

  useEffect(() => {
    fetchData(page);
  }, [page, fetchData]);

  useAuctionBroadcastWsHook(token, () => {
    fetchData(page);
  });

  const columns = [];
  if (viewType !== 'seller') {
    columns.push({ field: 'id', headerName: 'ID', width: 70 });
  }
  columns.push({ field: 'name', headerName: 'Name', flex: 1 });
  columns.push({ field: 'company_name', headerName: 'Company', flex: 1 });

  if (viewType !== 'seller') {
    columns.push({ field: 'mobile_number', headerName: 'Mobile Number', flex: 1 });
  }

  columns.push({
    field: 'amount',
    headerName: 'Bid Amount',
    flex: 1,
    renderCell: (params: any) => {
      return getFormattedPrice(params.value);
    },
  });

  if (viewType !== 'seller') {
    columns.push({
      field: 'is_winner',
      headerName: 'Is Winner',
      flex: 1,
      renderCell: (params: any) => {
        return params.value ? 'Yes' : 'No';
      },
    });
  }

  columns.push({
    field: 'created_at',
    headerName: 'Placed At',
    flex: 1,
    renderCell: (params: any) => {
      return getFormattedDate(params.value);
    },
  });
  columns.push({
    field: 'network_info',
    headerName: 'IP Address',
    sortable: false,
    flex: 1,
    renderCell: (params: any) => {
      return params.row?.network_info ? params.row?.network_info?.ip_address : '';
    },
  });

  // if (viewType !== 'seller') {
  //   columns.push({
  //     field: 'actions',
  //     headerName: 'Actions',
  //     width: 120,
  //     sortable: false,
  //     renderCell: (params: any) => (
  //       <Box>
  //         <IconButton color="primary" onClick={() => onEdit(params.row)}>
  //           <EditIcon />
  //         </IconButton>
  //         <IconButton color="error" onClick={() => onDelete(params.row)}>
  //           <DeleteIcon />
  //         </IconButton>
  //       </Box>
  //     ),
  //   });
  // }

  return (
    <Box sx={{ width: '100%' }}>
      <DataGrid
        rows={rows}
        columns={columns.map((col) => ({ ...col, sortable: false }))}
        pageSizeOptions={[pageSize]}
        paginationMode="server"
        paginationModel={{ page, pageSize }}
        rowCount={rowCount}
        onPaginationModelChange={(newModel: any) => setPage(newModel.page)}
        loading={loading}
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        slots={{
          loadingOverlay: DatagridLoader,
        }}
        sx={{
          // '& .MuiDataGrid-row': {
          //   maxHeight: 'inherit !important',
          //   '--height': '75px !important',
          // },
          '& .item-img-cell': {
            display: 'flex',
          },
        }}
      />
    </Box>
  );
};

export default AuctionBidGridWidget;
